import { useCallback, useEffect, useState } from "react";
import ErrorAlert from "../components/ErrorAlert";
import LoadingState from "../components/LoadingState";
import PageTitle from "../components/PageTitle";
import { adminApi } from "../services/adminApi";
import LocationForm from "./LocationForm";

const emptyLocation = {
  name: "",
  slug: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "United States",
  phone: "",
  email: "",
  accepts_pickup: true,
  accepts_delivery: false,
  accepts_catering: true,
  is_active: true,
  payment_settings: {
    stripe_enabled: true,
    cash_enabled: false,
    zelle_enabled: false,
    cashapp_enabled: false,
    sales_tax_rate: "0.0000",
    delivery_fee: "0.00",
    minimum_order: "0.00",
    service_fee: "0.00",
  },
};

function LocationEditPage({ locationId, onNavigate }) {
  const isNew = locationId === "new";
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(emptyLocation);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");

  const loadLocation = useCallback(async () => {
    if (isNew) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = await adminApi(`/locations/${locationId}/`);
      setForm(payload);
    } catch (requestError) {
      setError(requestError);
    } finally {
      setLoading(false);
    }
  }, [isNew, locationId]);

  useEffect(() => {
    loadLocation();
  }, [loadLocation]);

  function updateForm(changes) {
    setForm((currentForm) => ({ ...currentForm, ...changes }));
  }

  async function saveLocation(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setFieldErrors({});
    setSuccess("");

    try {
      const payload = await adminApi(isNew ? "/locations/" : `/locations/${locationId}/`, {
        method: isNew ? "POST" : "PATCH",
        body: JSON.stringify(form),
      });
      setForm(payload);
      setSuccess("Location saved.");
      if (isNew) {
        onNavigate(`/admin/locations/${payload.id}/edit`);
      }
    } catch (requestError) {
      setError(requestError);
      setFieldErrors(requestError.fieldErrors || {});
    } finally {
      setSaving(false);
    }
  }

  async function deactivateLocation() {
    setSaving(true);
    setError(null);

    try {
      await adminApi(`/locations/${locationId}/`, { method: "DELETE" });
      setSuccess("Location deactivated.");
      await loadLocation();
    } catch (requestError) {
      setError(requestError);
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="admin-page flex flex-column gap-5">
      <PageTitle eyebrow="Locations" title={isNew ? "Create Location" : "Edit Location"} />
      {error ? <ErrorAlert error={error} /> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}
      {loading ? <LoadingState /> : null}
      {!loading ? (
        <>
          <LocationForm
            fieldErrors={fieldErrors}
            form={form}
            saving={saving}
            onCancel={() => onNavigate("/admin/locations")}
            onChange={updateForm}
            onSubmit={saveLocation}
          />
          {!isNew ? (
            <button className="btn btn-danger" disabled={saving} onClick={deactivateLocation} type="button">
              Deactivate Location
            </button>
          ) : null}
        </>
      ) : null}
    </main>
  );
}

export default LocationEditPage;
